import { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

type PropTypes = {
  onCreate: (name: string) => void;
  disabled: boolean;
};

const dashedButtonSx = {
  fontSize: "14px",
  width: "100%",
  boxSizing: "border-box" as const,
  justifyContent: "center",
  alignItems: "center",
  color: "#1361A4",
  backgroundColor: "#e3f2fd",
  border: "1px dashed #1361A4",
  "&:hover": {
    backgroundColor: "#d0e7fb",
    border: "1px dashed #1361A4",
  },
  "& .MuiButton-startIcon": { margin: 0, mr: 0.5 },
};

/**
 * "+ Novo agrupamento" e o campo inline que ele abre.
 *
 * Mesmo padrão visual do botão "Novo fornecedor" do rodapé — nenhum componente
 * novo, só o `Button` do MUI com a borda tracejada.
 */
const NewGroupRow: React.FC<PropTypes> = ({ onCreate, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");

  const close = () => {
    setIsOpen(false);
    setName("");
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    close();
  };

  if (disabled) return null;

  if (!isOpen) {
    return (
      <Box sx={{ mt: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setIsOpen(true)}
          sx={dashedButtonSx}
        >
          Novo agrupamento
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 1,
        px: 1.5,
        py: 1,
        border: "1px solid #1361A4",
        borderRadius: "6px",
        backgroundColor: "#FFF",
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.5px",
          color: "#1361A4",
          backgroundColor: "#E6F1FB",
          border: "1px solid #C7DDF3",
          borderRadius: "4px",
          px: 0.75,
          py: "2px",
          whiteSpace: "nowrap",
        }}
      >
        AGRUPAMENTO
      </Box>

      <TextField
        autoFocus
        fullWidth
        size="small"
        value={name}
        placeholder="Nome do agrupamento (ex.: Dia 1, Pré-evento, Day Use)"
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            submit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            close();
          }
        }}
        inputProps={{ "aria-label": "Nome do novo agrupamento" }}
      />

      {/*
        `variant="outlined"` no cancelar, não o `text` com `color: #666`: o tema
        pinta o fundo do `Button` de azul, e o cinza escuro por cima ficava
        ilegível — "o botão continua com as letras escuras e o botão já azul,
        então fica difícil visualizar" (vídeo do QA). Mesmo par de botões do
        `NewRowForm`.
      */}
      <Button
        variant="outlined"
        size="small"
        onClick={close}
        sx={{ textTransform: "none" }}
      >
        Cancelar
      </Button>
      <Button
        variant="contained"
        size="small"
        onClick={submit}
        disabled={!name.trim()}
        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
      >
        Criar
      </Button>
    </Box>
  );
};

export default NewGroupRow;
